import { useState } from 'react';
import { ApiError } from '../../api/client.js';
import { Modal } from '../../components/Modal.js';
import { useCreatePosition, useInstrumentSearch } from '../../api/queries.js';

/**
 * Nimmt einen Wert ins Portfolio auf.
 *
 * Als Dialog und nicht mehr als Formular am Fuß der Seitenleiste: Dort stand es
 * dauerhaft im Weg, obwohl es selten gebraucht wird — ein Portfolio wächst in
 * Monaten, nicht in Minuten. In der Leiste bleibt ein Pluszeichen.
 *
 * Das Symbolfeld fragt die Suche des Anbieters ab, damit ein echtes Papier gewählt
 * wird statt eines Tickers, der erst beim Import scheitert. Ein frei eingetipptes
 * Symbol funktioniert weiterhin — der Server löst es auf.
 *
 * Kein Stop-Loss an dieser Stelle: Er folgt aus der Analyse des Charts und wird
 * dort über die Stop-Loss-Linie gesetzt. Ein Feld hier würde zu einer Zahl
 * verleiten, die noch auf nichts beruht.
 *
 * Die Felder liegen unmittelbar im Dialog, ohne eigenes `<form>`: `Modal` bringt
 * bereits eines mit, und ein verschachteltes wäre ungültig.
 */

interface AddPositionDialogProps {
  isOpen: boolean;
  onClose: () => void;
}

export const AddPositionDialog = ({
  isOpen,
  onClose,
}: AddPositionDialogProps): React.JSX.Element => {
  const [symbol, setSymbol] = useState('');
  const [quantity, setQuantity] = useState('');
  const [entryPrice, setEntryPrice] = useState('');
  const search = useInstrumentSearch(symbol);
  const createPosition = useCreatePosition();

  const schliessen = (): void => {
    createPosition.reset();
    onClose();
  };

  const submit = (): void => {
    const trimmed = symbol.trim();
    if (trimmed.length === 0) return;

    /* Komma als Dezimaltrennzeichen zulassen — hier wird deutsch getippt. */
    const toNumber = (value: string): number => Number.parseFloat(value.replace(',', '.'));
    const parsedQuantity = toNumber(quantity);
    const parsedEntryPrice = toNumber(entryPrice);

    createPosition.mutate(
      {
        symbol: trimmed,
        ...(Number.isFinite(parsedQuantity) && parsedQuantity > 0
          ? { quantity: parsedQuantity }
          : {}),
        ...(Number.isFinite(parsedEntryPrice) && parsedEntryPrice > 0
          ? { entryPrice: parsedEntryPrice }
          : {}),
      },
      {
        onSuccess: () => {
          setSymbol('');
          setQuantity('');
          setEntryPrice('');
          onClose();
        },
      },
    );
  };

  const error = createPosition.error;
  const message = error instanceof ApiError ? error.message : error ? 'Unbekannter Fehler' : null;

  return (
    <Modal
      isOpen={isOpen}
      title="Wert hinzufügen"
      onClose={schliessen}
      actions={
        <>
          <button type="button" onClick={schliessen} disabled={createPosition.isPending}>
            Abbrechen
          </button>
          <button
            type="button"
            className="button-primary"
            onClick={submit}
            disabled={createPosition.isPending || symbol.trim().length === 0}
          >
            {createPosition.isPending ? 'Fügt hinzu …' : 'Hinzufügen'}
          </button>
        </>
      }
    >
      <div className="add-position__row">
        <label className="field field--grow">
          <span className="field__label">Symbol</span>
          <input
            value={symbol}
            onChange={(event) => setSymbol(event.target.value)}
            placeholder="z. B. APC.DE"
            list="symbol-suggestions"
            autoComplete="off"
            /* Der Dialog öffnet sich für genau diese Eingabe. */
            autoFocus
          />
          <datalist id="symbol-suggestions">
            {(search.data ?? []).map((result) => (
              <option key={`${result.symbol}-${result.exchange}`} value={result.symbol}>
                {result.name}
                {result.exchange ? ` · ${result.exchange}` : ''}
              </option>
            ))}
          </datalist>
        </label>

        <label className="field field--narrow">
          <span className="field__label">Stück</span>
          <input
            type="number"
            min="0"
            step="any"
            value={quantity}
            onChange={(event) => setQuantity(event.target.value)}
            placeholder="optional"
          />
        </label>

        <label className="field field--narrow">
          <span className="field__label">Ø Einstand</span>
          <input
            type="number"
            min="0"
            step="any"
            value={entryPrice}
            onChange={(event) => setEntryPrice(event.target.value)}
            placeholder="optional"
          />
        </label>
      </div>

      <p className="muted small add-position__hinweis">
        Die Kurshistorie wird beim Anlegen geholt — das dauert einen Moment. Den Stop-Loss legst du
        anschließend im Chart fest, indem du die Stop-Loss-Linie auf den gewünschten Kurs ziehst.
      </p>

      {message ? <p className="hint hint--error">{message}</p> : null}
    </Modal>
  );
};
